import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  MessagesResponse,
  acceptPlan,
  answerQuestions,
  deleteAIBuilderHistory,
  interruptAIBuilder,
  loadAIBuilderMessages,
  rejectPlan,
  runAIBuilder,
  transcribeAudio,
  updateSessionMode,
} from './requests';

const getAiBuilderMessagesQueryKey = (journeyShortId?: string, chatId?: string) => [
  'ai-builder-messages',
  journeyShortId,
  chatId,
];

export const useInvalidateAiBuilderMessages = () => {
  const queryClient = useQueryClient();

  const invalidateMessages = (journeyShortId: string, chatId: string) => {
    queryClient.invalidateQueries(getAiBuilderMessagesQueryKey(journeyShortId, chatId));
  };

  return {
    invalidateMessages,
  };
};

export const useRunAIBuilder = () => {
  return useMutation({
    mutationFn: runAIBuilder,
  });
};

export const useInterruptAIBuilder = () => {
  return useMutation({
    mutationFn: interruptAIBuilder,
  });
};

export const useAIBuilderMessages = (journeyShortId: string, chatId: string) => {
  return useQuery<MessagesResponse>({
    queryKey: getAiBuilderMessagesQueryKey(journeyShortId, chatId),
    queryFn: () => loadAIBuilderMessages(journeyShortId, chatId),
  });
};

export const useDeleteAIBuilderHistory = (journeyShortId: string, chatId: string) => {
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: () => deleteAIBuilderHistory(journeyShortId, chatId),
    onSuccess: () => {
      invalidateMessages(journeyShortId, chatId);
    },
  });
};

export const useTranscribeAudio = () => {
  return useMutation({
    mutationFn: transcribeAudio,
  });
};

export const useAnswerQuestions = () => {
  return useMutation({
    mutationFn: answerQuestions,
  });
};

export const useUpdateSessionMode = () => {
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: updateSessionMode,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId, variables.chatId);
    },
  });
};

export const useAcceptPlan = () => {
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: acceptPlan,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId, variables.chatId);
    },
  });
};

export const useRejectPlan = () => {
  const { invalidateMessages } = useInvalidateAiBuilderMessages();

  return useMutation({
    mutationFn: rejectPlan,
    onSuccess: (_, variables) => {
      invalidateMessages(variables.journeyShortId, variables.chatId);
    },
  });
};
